import type React from "react";

import { useState } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import { Input } from "@/shadcnComponents/ui/input";
import { Card, CardContent } from "@/shadcnComponents/ui/card";
import { Alert, AlertDescription } from "@/shadcnComponents/ui/alert";
import { Link } from "react-router-dom";
import AppSumoPlan from "./appsumo-plan";
import { useCoupon } from "@/Providers/Coupon";

export default function AppSumoDiscountCodePage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero Section - Only Discount Code Activation */}
      <section className="w-full pt-10 sm:pt-14 md:pt-16 lg:pt-20 pb-12 sm:pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 text-center">
          <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Activate Coupon Code
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto mb-8 sm:mb-10">
            Enter the code you got from AppSumo, and gain full access to anchors
            for performance-based influencer marketing
          </p>

          <div className="w-full max-w-md mx-auto">
            <CodeActivationForm />
          </div>
        </div>
      </section>

      {/* AppSumo Plan Section - Separate from Hero */}
      <section className="w-full bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-3 sm:px-4">
          <AppSumoPlan />
        </div>
      </section>
    </div>
  );
}

function CodeActivationForm() {
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activationStatus, setActivationStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const couponState = useCoupon();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setErrorMessage("Please enter a discount code");
      setActivationStatus("error");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await couponState?.checkAndVerifyCouponCodeForAppSumo(
        code,
        "6814732fe4c145ec91ace5a3"
      );
      if (result?.success) {
        setActivationStatus("success");
        const now = new Date();
        const expiry = now.getTime() + 24 * 60 * 60 * 1000; // 24 hours in ms
        const data = {
          code: code.toUpperCase(),
          expiry,
        };
        localStorage.setItem("appsumocode", JSON.stringify(data));
        setIsSubmitting(false);
      } else {
        setErrorMessage(result?.error);
        setActivationStatus("error");
        setIsSubmitting(false);
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again.");
      setActivationStatus("error");
      setIsSubmitting(false);
    }

    // setTimeout(() => {
    //   const regex = /^appsumo([1-9][0-9]{0,2}|1000|1001)$/i;
    //   if (regex.test(code)) {
    //     setActivationStatus("success");
    //     const now = new Date();
    //     const expiry = now.getTime() + 24 * 60 * 60 * 1000; // 24 hours in ms
    //     const data = {
    //       code: code.toUpperCase(),
    //       expiry,
    //     };
    //     localStorage.setItem("appsumocode", JSON.stringify(data));
    //   } else {
    //     setErrorMessage("Invalid discount code. Please try again.");
    //     setActivationStatus("error");
    //   }
    //   setIsSubmitting(false);
    // }, 1000);
  };

  const resetForm = () => {
    setCode("");
    setActivationStatus("idle");
    setErrorMessage("");
  };

  return (
    <Card className="shadow-md border-gray-200">
      <CardContent className="pt-3 xs:pt-4 sm:pt-5 md:pt-6 px-2 xs:px-3 sm:px-4 md:px-6">
        {activationStatus === "idle" && (
          <form
            onSubmit={handleSubmit}
            className="space-y-2 xs:space-y-3 sm:space-y-4"
          >
            <div className="space-y-1 sm:space-y-2">
              <label
                htmlFor="discount-code"
                className="text-xs sm:text-sm font-medium text-gray-700 text-left block"
              >
                Enter discount code
              </label>
              <Input
                id="discount-code"
                placeholder="Enter your code (e.g. APPSUMO2024)"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="w-full border-gray-300 text-xs xs:text-sm sm:text-base h-8 xs:h-9 sm:h-10"
                autoComplete="off"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-red-500 hover:bg-red-600 text-white text-xs xs:text-sm sm:text-base h-8 xs:h-9 sm:h-10"
              disabled={isSubmitting}
              variant="destructive"
            >
              {isSubmitting ? "Activating..." : "Activate Code"}
            </Button>
          </form>
        )}

        {activationStatus === "success" && (
          <div className="space-y-4 sm:space-y-5">
            <div className="p-4 sm:p-5 border border-dashed border-green-300 rounded-md bg-green-50 text-center">
              <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-green-500 mx-auto mb-2 sm:mb-3" />
              <p className="text-xl sm:text-2xl font-bold text-green-700">
                All set! Your code is active
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm sm:text-base text-gray-600">
                Log in or sign up to start your first influencer campaign. Zero
                guesswork, fully performance-based.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Link
                  to="/brand/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button
                    className="w-full bg-red-500 hover:bg-red-600 text-white text-sm h-10 sm:h-12"
                    variant="destructive"
                  >
                    Login
                  </Button>
                </Link>
                <Link
                  to="/brand/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full"
                >
                  <Button
                    variant="outline"
                    className="w-full border-red-200 hover:bg-red-50 text-red-500 hover:text-red-600 text-sm h-10 sm:h-12"
                  >
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {activationStatus === "error" && (
          <div className="space-y-4 sm:space-y-5">
            <div className="bg-red-50 flex items-center justify-center rounded-sm border-red-200 py-3">
              <XCircle className="h-5 w-5 text-red-500" />
              <AlertDescription className="ml-2 text-red-800 text-sm">
                {errorMessage}
              </AlertDescription>
            </div>
            <Button
              className="w-full bg-red-500 hover:bg-red-600 text-white text-sm h-10 sm:h-12"
              onClick={resetForm}
              variant="destructive"
            >
              Try Again
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Navbar() {
  return (
    <header className="w-full px-4 sm:px-3 md:px-4 py-1 sm:py-2 mt-2 sm:mt-3 md:mt-5">
      <div
        className="w-full max-w-full sm:max-w-xl md:max-w-3xl lg:max-w-4xl mx-auto flex items-center h-8 sm:h-10 md:h-12 lg:h-14 px-4 sm:px-3 md:px-4 rounded-md sm:rounded-lg"
        style={{ backgroundColor: "rgba(122, 122, 122, 0.7)" }}
      >
        <Link
          to="https://www.anchors.in/brand/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center"
        >
          <img
            src="https://anchors-assets.s3.ap-south-1.amazonaws.com/anchors+logo+with+white+text.png"
            alt="Anchors Logo"
            width={120}
            height={28}
            className="w-16 xs:w-20 sm:w-24 md:w-28 lg:w-32 h-auto object-contain"
          />
        </Link>
      </div>
    </header>
  );
}
